import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

const VALID_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const DAY_LABELS: Record<string, string> = {
  monday: "Lunes",
  tuesday: "Martes",
  wednesday: "Miércoles",
  thursday: "Jueves",
  friday: "Viernes",
  saturday: "Sábado",
  sunday: "Domingo",
};

export const setProductScheduleSkill: Skill = {
  id: "setProductSchedule",
  description:
    "Configura los días de la semana en los que un producto está disponible. Vacío = todos los días.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      productId: { type: "string" },
      days: { type: "array", items: { type: "string", enum: [...VALID_DAYS] } },
    },
    required: ["restaurantId", "productId", "days"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const productId = String(args.productId ?? "");
    if (!restaurantId || !productId) return { ok: false, error: "Faltan IDs." };
    const days = Array.isArray(args.days)
      ? args.days
          .map(String)
          .filter((d) => VALID_DAYS.includes(d as (typeof VALID_DAYS)[number]))
      : [];

    const dayList = days.length === 0 ? "Todos los días" : days.map((d) => DAY_LABELS[d]).join(", ");

    const ok = await ctx.confirmation.confirm({
      title: "Configurar horario del producto",
      details: [{ label: "Días disponibles", value: dayList }],
      confirmLabel: "Aplicar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Configurando horario…",
      successMsg: "✓ Horario actualizado",
      restaurantId,
      targetTab: "horario-semanal",
      targetTabLabel: "Horario Semanal",
      run: async () => {
        try {
          await apiWithAuth.patch(
            `/restaurants/${restaurantId}/products/${productId}`,
            { scheduledDays: days },
            ctx.accessToken,
          );
          emitCopilotMutation({
            scope: "products",
            action: "updated",
            restaurantId,
            id: productId,
          });
          return {
            ok: true as const,
            result: { productId, days },
            humanSummary: `Horario: ${dayList}.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const setDayPromotionSkill: Skill = {
  id: "setDayPromotion",
  description:
    "Configura un descuento (promoción) de un producto para un día específico de la semana.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      productId: { type: "string" },
      day: { type: "string", enum: [...VALID_DAYS] },
      discountType: { type: "string", enum: ["percent", "fixed"] },
      discountValue: { type: "number" },
      isActive: { type: "boolean" },
    },
    required: ["restaurantId", "productId", "day", "discountType", "discountValue"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const productId = String(args.productId ?? "");
    const day = String(args.day ?? "");
    const discountType = String(args.discountType ?? "");
    const discountValue = Number(args.discountValue);
    if (!restaurantId || !productId || !VALID_DAYS.includes(day as (typeof VALID_DAYS)[number])) {
      return { ok: false, error: "Datos inválidos." };
    }
    if (!["percent", "fixed"].includes(discountType) || !Number.isFinite(discountValue) || discountValue < 0) {
      return { ok: false, error: "discountType o discountValue inválidos." };
    }
    const isActive = args.isActive !== false;

    const valueLabel =
      discountType === "percent"
        ? `${discountValue}% de descuento`
        : `$${discountValue.toLocaleString()} de descuento`;

    const ok = await ctx.confirmation.confirm({
      title: "Configurar promoción del día",
      details: [
        { label: "Día", value: DAY_LABELS[day] },
        { label: "Descuento", value: valueLabel },
        { label: "Activa", value: isActive ? "Sí" : "No" },
      ],
      confirmLabel: "Aplicar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Configurando promo ${DAY_LABELS[day]}…`,
      successMsg: "✓ Promoción configurada",
      restaurantId,
      targetTab: "horario-semanal",
      targetTabLabel: "Horario Semanal",
      run: async () => {
        try {
          await apiWithAuth.put(
            `/restaurants/${restaurantId}/products/${productId}/promotions/${day}`,
            { discountType, discountValue, isActive },
            ctx.accessToken,
          );
          emitCopilotMutation({
            scope: "products",
            action: "updated",
            restaurantId,
            id: productId,
          });
          return {
            ok: true as const,
            result: { productId, day, discountType, discountValue },
            humanSummary: `${DAY_LABELS[day]}: ${valueLabel}.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const removeDayPromotionSkill: Skill = {
  id: "removeDayPromotion",
  description:
    "Elimina la promoción configurada para un día específico de un producto.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      productId: { type: "string" },
      day: { type: "string", enum: [...VALID_DAYS] },
    },
    required: ["restaurantId", "productId", "day"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const productId = String(args.productId ?? "");
    const day = String(args.day ?? "");
    if (!restaurantId || !productId || !VALID_DAYS.includes(day as (typeof VALID_DAYS)[number])) {
      return { ok: false, error: "Datos inválidos." };
    }

    const ok = await ctx.confirmation.confirm({
      title: "Eliminar promoción",
      details: [{ label: "Día", value: DAY_LABELS[day] }],
      confirmLabel: "Eliminar",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Quitando promo ${DAY_LABELS[day]}…`,
      successMsg: "✓ Promoción eliminada",
      restaurantId,
      targetTab: "horario-semanal",
      targetTabLabel: "Horario Semanal",
      run: async () => {
        try {
          await apiWithAuth.delete(
            `/restaurants/${restaurantId}/products/${productId}/promotions/${day}`,
            ctx.accessToken,
          );
          emitCopilotMutation({
            scope: "products",
            action: "updated",
            restaurantId,
            id: productId,
          });
          return {
            ok: true as const,
            result: { productId, day, removed: true },
            humanSummary: `Promoción de ${DAY_LABELS[day]} eliminada.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const listDayPromotionsSkill: Skill = {
  id: "listDayPromotions",
  description:
    "Lista todas las promociones configuradas por día para un producto.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, productId: { type: "string" } },
    required: ["restaurantId", "productId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const productId = String(args.productId ?? "");
    if (!restaurantId || !productId) return { ok: false, error: "Faltan IDs." };
    return withFollow(ctx, {
      intro: "Consultando promociones…",
      successMsg: "✓ Promociones obtenidas",
      run: async () => {
        try {
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/products/${productId}/promotions`,
            ctx.accessToken,
          );
          return { ok: true as const, result: data };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
